import { type FunctionComponent } from "react";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, FileTextOutlined } from "@ant-design/icons";
import { Button, Popover, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_AI_AUTOMATION_TASK_SENDER,
  type ScheduledAiReportAutomation,
} from "@fitness-app/data-models/entities/AutomationTemplate";
import { ProgramAutomationTaskStatus } from "@fitness-app/data-models/entities/ProgramAutomation";

import { formatSentTime } from "~/modules/Automation/utils/formatSentTime";
import { automationTaskStatusBadge } from "~/modules/Automation/utils/automationTaskStatusBadge";

interface OwnProps {
  item: ScheduledAiReportAutomation;
  onEdit?: () => void;
  onRemove: () => void;
  /** Present in the client's calendar (a scheduled task); absent in the template table. */
  status?: ProgramAutomationTaskStatus;
  sentDate?: string;
}

type Props = OwnProps;

const AiReportItem: FunctionComponent<Props> = ({ item, onEdit, onRemove, status, sentDate }) => {
  const { t } = useTranslation("automation");
  const sentTimeLabel = formatSentTime(item.sentTime, sentDate);
  const isDone = status === ProgramAutomationTaskStatus.Done;
  const sender = item.sender ?? DEFAULT_AI_AUTOMATION_TASK_SENDER;

  return (
    <Popover
      title={t("programAutomation.aiReportTask")}
      content={
        <Space direction="vertical" className="min-w-[220px] max-w-[320px]">
          {status && (
            <Space>
              <Typography.Text type="secondary">{t("events.status")}</Typography.Text>
              {automationTaskStatusBadge(status, t)}
            </Space>
          )}
          <Space>
            <Typography.Text type="secondary">{t("programAutomation.aiReport.name")}:</Typography.Text>
            <Typography.Text>{item.name}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("programAutomation.aiReport.range")}:</Typography.Text>
            <Typography.Text>{t("programAutomation.aiReport.lastDays", { count: item.days })}</Typography.Text>
          </Space>
          {item.fallbackMessage && (
            <Space align="start">
              <Typography.Text type="secondary">{t("programAutomation.aiReport.fallbackMessage")}:</Typography.Text>
              <Typography.Text>{item.fallbackMessage}</Typography.Text>
            </Space>
          )}
          <Space>
            <Typography.Text type="secondary">{t("programAutomation.taskSender.label")}:</Typography.Text>
            <Typography.Text>
              {t(`programAutomation.taskSender.${sender}`)} ({t(`programAutomation.taskSender.${sender}Hint`)})
            </Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">{t("automatedMessageForm.operationTime")}:</Typography.Text>
            <Typography.Text>{sentTimeLabel}</Typography.Text>
          </Space>
          {isDone && (
            <Typography.Text type="secondary">{t(`programAutomation.taskSender.doneHint.${sender}`)}</Typography.Text>
          )}
          {!isDone && (
            <Space>
              {onEdit && <Button icon={<EditOutlined />} type="text" onClick={onEdit} />}
              <Button danger icon={<DeleteOutlined />} type="text" onClick={onRemove} />
            </Space>
          )}
        </Space>
      }
    >
      <span className="cursor-pointer">
        <FileTextOutlined className="mr-2" />
        <span>{sentTimeLabel}</span>
        <Tag color="purple" className="ml-2">
          AI
        </Tag>
        {isDone && <CheckOutlined className="ml-1 text-green-500" />}
        {status === ProgramAutomationTaskStatus.Canceled && <CloseOutlined className="ml-1 text-orange-500" />}
        <div className="max-w-[140px] truncate text-xs text-gray-500">{item.name}</div>
        <div className="max-w-[140px] truncate text-xs text-gray-400">
          {t(`programAutomation.taskSender.${sender}`)}
        </div>
      </span>
    </Popover>
  );
};

export default AiReportItem;
